import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight } from "@phosphor-icons/react";
import { TRENT_LOGO_URL } from "@/components/Logo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", consent: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) { setError("Please accept the Privacy Policy and Terms of Service."); return; }
    setLoading(true);
    try {
      const u = await register(form.name, form.email, form.password, true);
      // signup is always customer; admins bounce to admin dashboard if seeded that way
      navigate(u.role === "admin" ? "/admin-dashboard" : "/customer-dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-md p-8" data-testid="register-form">
        <div className="flex items-center gap-3 mb-6">
          <img src={TRENT_LOGO_URL} alt="Trent" className="h-10 w-auto" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#475569] mb-2">Customer sign up</div>
        <h2 className="trent-heading text-2xl font-black tracking-tighter text-[#0F172A] mb-6">
          Create your customer account
        </h2>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Full name</span>
          <input value={form.name} onChange={upd("name")} required className="trent-input mt-1 mb-3" data-testid="register-name-input" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Email</span>
          <input type="email" value={form.email} onChange={upd("email")} required className="trent-input mt-1 mb-3" data-testid="register-email-input" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Phone (optional)</span>
          <input type="tel" value={form.phone} onChange={upd("phone")} placeholder="+353…" className="trent-input mt-1 mb-3" data-testid="register-phone-input" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Password</span>
          <input type="password" value={form.password} onChange={upd("password")} required minLength={6} className="trent-input mt-1 mb-3" data-testid="register-password-input" />
        </label>

        <label className="flex items-start gap-2 mt-3 mb-1 text-xs text-[#475569]">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) => setForm({ ...form, consent: e.target.checked })}
            className="mt-1"
            data-testid="register-consent"
            required
          />
          <span>
            I have read and accept the{" "}
            <Link to="/privacy" className="text-[#16A34A] underline font-semibold">Privacy Policy</Link>{" "}
            and{" "}
            <Link to="/terms" className="text-[#16A34A] underline font-semibold">Terms of Service</Link>.
            I consent to Trent Property Management storing my personal data (name, email, property &
            inspection records) under Irish/EU GDPR.
          </span>
        </label>

        {error && (
          <div className="my-3 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FCA5A5] px-3 py-2 rounded-sm" data-testid="register-consent-error">
            {error}
          </div>
        )}

        <button type="submit" disabled={loading} className="trent-btn-primary w-full mt-4" data-testid="register-submit-btn">
          {loading ? "Creating..." : "Create account"} <ArrowRight weight="bold" size={16} />
        </button>

        <div className="mt-6 text-sm text-[#475569]">
          Already have an account?{" "}
          <Link to="/login" className="text-[#0F172A] font-semibold underline" data-testid="login-link">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
